import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart3, Loader2 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { useOrg } from "@/hooks/useOrg";
import { kindBadge } from "@/lib/kinds";
import { api } from "@/lib/api";

const STALE_DAYS = 14;

// ponytail: inline relative-time — one call site, not worth a shared util module.
function timeAgo(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isStale(iso) {
  if (!iso) return true;
  const days = (Date.now() - new Date(iso).getTime()) / 86400000;
  return days > STALE_DAYS;
}

export default function OrgAnalytics() {
  const { slug } = useParams();
  const { org } = useOrg(slug);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!slug) return;
    api.get(`/orgs/${slug}/analytics`).then(({ data }) => setData(data)).catch(() => setData({ artifacts: [], members: [], seat_count: 0 }));
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-[color:var(--rb-accent)]" />
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
          Adoption{org ? ` — ${org.name}` : ""}
        </h1>
      </div>
      <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
        Which published artifacts are actually installed, and who's fallen behind on syncing.
      </p>

      {data === null ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[color:var(--rb-text-faint)]" />
        </div>
      ) : (
        <>
          <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--rb-border)]">
            <table className="w-full text-sm" data-testid="analytics-artifacts-table">
              <thead className="bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text-faint)]">
                <tr>
                  <th className="px-4 py-2 text-left">Artifact</th>
                  <th className="px-4 py-2 text-left">Version</th>
                  <th className="px-4 py-2 text-left">Installed</th>
                  <th className="px-4 py-2 text-left">Last fetched</th>
                </tr>
              </thead>
              <tbody>
                {data.artifacts.map((a) => (
                  <tr key={a.artifact_id} data-testid="analytics-artifact-row" className="border-t border-[color:var(--rb-border)]">
                    <td className="px-4 py-2 text-[color:var(--rb-text)]">
                      {a.name}
                      {kindBadge(a.kind) && (
                        <span className="ml-2 rounded-full border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-faint)]">
                          {kindBadge(a.kind)}
                        </span>
                      )}
                    </td>
                    <td className="rb-mono px-4 py-2 text-[color:var(--rb-text-dim)]">v{a.current_version}</td>
                    <td className="px-4 py-2 text-[color:var(--rb-text-dim)]">
                      Installed on {a.seats_installed} of {data.seat_count} seats
                      {a.seats_current !== a.seats_installed ? ` (${a.seats_current} on current)` : ""}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--rb-text-faint)]">{timeAgo(a.last_fetched_at)}</td>
                  </tr>
                ))}
                {data.artifacts.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-xs text-[color:var(--rb-text-faint)]">
                      Nothing published yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="mt-8 text-sm font-medium text-[color:var(--rb-text)]">Members</h2>
          <div className="mt-2 overflow-hidden rounded-2xl border border-[color:var(--rb-border)]">
            <table className="w-full text-sm" data-testid="analytics-members-table">
              <thead className="bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text-faint)]">
                <tr>
                  <th className="px-4 py-2 text-left">Member</th>
                  <th className="px-4 py-2 text-left">Last synced</th>
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.user_id} data-testid="analytics-member-row" className="border-t border-[color:var(--rb-border)]">
                    <td className="px-4 py-2 text-[color:var(--rb-text)]">{m.username ? `@${m.username}` : "—"}</td>
                    <td className="px-4 py-2 text-[color:var(--rb-text-dim)]">
                      {timeAgo(m.last_sync_at)}
                      {isStale(m.last_sync_at) && (
                        <span
                          data-testid="analytics-stale-badge"
                          className="ml-2 rounded-full bg-[color:rgba(212,175,55,0.14)] px-2 py-0.5 text-[10px] text-[color:var(--rb-gold)]"
                        >
                          stale
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {data.members.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-4 py-6 text-center text-xs text-[color:var(--rb-text-faint)]">No active members.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppShell>
  );
}
